/**
 * WLED Devices Store
 * Manages WLED LED strip controller devices
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { getDataProvider, subscribeToTable, type Subscription, type ChangePayload, TABLES } from '@/lib/dataProvider'
import { useAuthStore } from './auth'
import { wledLogger } from '@/services/wled/WledDeviceService'
import { wledDiscoveryService, type DiscoveredWledDevice } from '@/services/wled/WledDiscoveryService'
import { wledDeviceService } from '@/services/wled/WledDeviceService'
import { Platform } from '@/lib/platform'

export interface WledDevice {
  id: string
  user_id: string
  mac_address: string
  name: string
  ip_address: string
  led_count: number
  firmware_version: string | null
  architecture: string | null
  is_online: boolean
  is_active: boolean
  last_seen: string | null
  created_at: string
  updated_at: string
}

export interface WledDeviceWithStatus extends WledDevice {
  isReachable: boolean
  lastChecked: number | null
}

export const useWledDevicesStore = defineStore('wledDevices', () => {
  // State
  const devices = ref<Map<string, WledDevice>>(new Map())
  const discoveredDevices = ref<Map<string, DiscoveredWledDevice>>(new Map())
  const deviceStatuses = ref<Map<string, { isReachable: boolean; lastChecked: number }>>(new Map())
  
  // Realtime subscription
  let realtimeSubscription: Subscription | null = null
  
  // Loading states
  const isDiscovering = ref(false)
  const isLoading = ref(false)
  const isConnecting = ref(false)
  
  // Error state
  const error = ref<string | null>(null)

  // Getters - access .size to track Map changes for proper reactivity
  const allDevices = computed(() => {
    // Reading .size ensures Vue tracks Map mutations
    const size = devices.value.size
    return Array.from(devices.value.values())
  })
  
  const devicesWithStatus = computed((): WledDeviceWithStatus[] => {
    return allDevices.value.map(device => {
      const status = deviceStatuses.value.get(device.id)
      return {
        ...device,
        isReachable: status?.isReachable ?? false,
        lastChecked: status?.lastChecked ?? null
      }
    })
  })

  const onlineDevices = computed(() => 
    devicesWithStatus.value.filter(device => device.is_online)
  )

  const activeDevices = computed(() => 
    allDevices.value.filter(device => device.is_active)
  )

  const availableDiscovered = computed(() => 
    Array.from(discoveredDevices.value.values())
      .filter(discovered => !Array.from(devices.value.values())
        .some(d => d.mac_address === discovered.macAddress))
  )

  // Actions
  const setError = (message: string | null) => {
    error.value = message
  }

  const clearError = () => {
    error.value = null
  }

  /**
   * Load WLED devices from database
   */
  const fetchDevices = async (): Promise<void> => {
    const authStore = useAuthStore()
    if (!authStore.user) {
      setError('User not authenticated')
      return
    }

    try {
      isLoading.value = true
      clearError()

      const provider = getDataProvider()
      const data = await provider.query<WledDevice>(TABLES.WLED_DEVICES, {
        orderBy: [{ column: 'created_at', ascending: false }]
      })

      // Update devices map
      devices.value.clear()
      for (const device of data || []) {
        devices.value.set(device.id, device)
      }

      wledLogger.info(`Loaded ${devices.value.size} WLED devices`)

      // Check connectivity for all devices
      await checkDeviceConnectivity()

    } catch (err) {
      wledLogger.error('Failed to fetch WLED devices:', err)
      setError(err instanceof Error ? err.message : 'Failed to load WLED devices')
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Discover WLED devices on the network
   */
  const discoverDevices = async (): Promise<void> => {
    if (!Platform.isTauri()) {
      setError('WLED discovery only available in Tauri app')
      return
    }

    try {
      isDiscovering.value = true
      clearError()

      const discovered = await wledDiscoveryService.discoverDevices()
      
      discoveredDevices.value.clear()
      for (const device of discovered) {
        discoveredDevices.value.set(device.macAddress, device)
      }

      wledLogger.info(`Discovered ${discovered.length} WLED devices`)
    } catch (err) {
      wledLogger.error('Device discovery failed:', err)
      setError(err instanceof Error ? err.message : 'Discovery failed')
    } finally {
      isDiscovering.value = false
    }
  }

  /**
   * Register a discovered WLED device
   */
  const registerDevice = async (discovered: DiscoveredWledDevice): Promise<WledDevice | null> => {
    const authStore = useAuthStore()
    if (!authStore.user) {
      setError('User not authenticated')
      return null
    }

    try {
      isConnecting.value = true
      clearError()

      // Get full device info
      const fullInfo = await wledDeviceService.getFullJson(discovered.ipAddress)
      if (!fullInfo) {
        setError('Could not connect to device')
        return null
      }

      const provider = getDataProvider()
      const result = await provider.insert<WledDevice>(TABLES.WLED_DEVICES, {
        user_id: authStore.user.id,
        mac_address: discovered.macAddress,
        name: discovered.name || fullInfo.info.name,
        ip_address: discovered.ipAddress,
        led_count: fullInfo.info.leds.count,
        firmware_version: fullInfo.info.ver,
        architecture: fullInfo.info.arch,
        is_online: true,
        is_active: true,
        last_seen: new Date().toISOString()
      })

      if (!result.success || !result.data) {
        setError(result.error || 'Failed to register device')
        return null
      }

      // Add to local state
      devices.value.set(result.data.id, result.data)
      deviceStatuses.value.set(result.data.id, { isReachable: true, lastChecked: Date.now() })

      // Remove from discovered
      discoveredDevices.value.delete(discovered.macAddress)

      wledLogger.success(`Registered WLED device: ${result.data.name}`)
      return result.data
    } catch (err) {
      wledLogger.error('Failed to register device:', err)
      setError(err instanceof Error ? err.message : 'Registration failed')
      return null
    } finally {
      isConnecting.value = false
    }
  }

  /**
   * Add a device manually by IP address
   */
  const addDeviceByIp = async (ipAddress: string): Promise<WledDevice | null> => {
    try {
      isConnecting.value = true
      clearError()

      // Probe the device
      const discovered = await wledDiscoveryService.probeDevice(ipAddress)
      if (!discovered) {
        setError('No WLED device found at this IP address')
        return null
      }

      return await registerDevice(discovered)
    } catch (err) {
      wledLogger.error('Failed to add device by IP:', err)
      setError(err instanceof Error ? err.message : 'Failed to add device')
      return null
    } finally {
      isConnecting.value = false
    }
  }

  /**
   * Update device information
   */
  const updateDevice = async (id: string, updates: Partial<WledDevice>): Promise<boolean> => {
    try {
      const provider = getDataProvider()
      const result = await provider.update<WledDevice>(TABLES.WLED_DEVICES, id, updates)

      if (!result.success) {
        setError(result.error || 'Failed to update device')
        return false
      }

      // Update local state
      const existing = devices.value.get(id)
      if (existing && result.data) {
        devices.value.set(id, result.data)
      }

      return true
    } catch (err) {
      wledLogger.error('Failed to update device:', err)
      setError(err instanceof Error ? err.message : 'Update failed')
      return false
    }
  }

  /**
   * Update device online status
   */
  const updateDeviceStatus = async (id: string, isOnline: boolean): Promise<void> => {
    const existing = devices.value.get(id)
    if (!existing) return

    // Update status tracking
    deviceStatuses.value.set(id, { isReachable: isOnline, lastChecked: Date.now() })

    // Only update DB if status changed
    if (existing.is_online !== isOnline) {
      await updateDevice(id, {
        is_online: isOnline,
        last_seen: isOnline ? new Date().toISOString() : existing.last_seen
      })
    }
  }

  /**
   * Remove a WLED device
   */
  const removeDevice = async (id: string): Promise<boolean> => {
    wledLogger.info(`[removeDevice] Starting removal for device: ${id}`)
    try {
      const provider = getDataProvider()
      wledLogger.info(`[removeDevice] Provider mode: ${provider.getMode()}`)
      
      const result = await provider.delete(TABLES.WLED_DEVICES, id)
      wledLogger.info(`[removeDevice] Delete result:`, result)

      if (!result.success) {
        setError(result.error || 'Failed to remove device')
        wledLogger.error(`[removeDevice] Delete failed:`, result.error)
        return false
      }

      // Remove from local state
      const hadDevice = devices.value.has(id)
      devices.value.delete(id)
      deviceStatuses.value.delete(id)
      
      wledLogger.info(`[removeDevice] Removed from local state. Had device: ${hadDevice}, Remaining devices: ${devices.value.size}`)
      return true
    } catch (err) {
      wledLogger.error('Failed to remove device:', err)
      setError(err instanceof Error ? err.message : 'Removal failed')
      return false
    }
  }

  /**
   * Check connectivity to all registered devices
   */
  const checkDeviceConnectivity = async (): Promise<void> => {
    if (!Platform.isTauri()) return

    for (const device of devices.value.values()) {
      const isReachable = await wledDeviceService.isWledDevice(device.ip_address)
      await updateDeviceStatus(device.id, isReachable)
    }
  }

  /**
   * Get a device by ID
   */
  const getDevice = (id: string): WledDevice | undefined => {
    return devices.value.get(id)
  }

  /**
   * Initialize realtime subscription
   */
  const initRealtimeSubscription = (): Subscription | null => {
    if (realtimeSubscription) {
      return realtimeSubscription
    }

    realtimeSubscription = subscribeToTable<WledDevice>(
      TABLES.WLED_DEVICES,
      (payload: ChangePayload<WledDevice>) => {
        wledLogger.debug('WLED devices realtime event:', payload.eventType)

        if (payload.eventType === 'INSERT' && payload.new) {
          devices.value.set(payload.new.id, payload.new)
        } else if (payload.eventType === 'UPDATE' && payload.new) {
          devices.value.set(payload.new.id, payload.new)
        } else if (payload.eventType === 'DELETE' && payload.old) {
          devices.value.delete(payload.old.id)
          deviceStatuses.value.delete(payload.old.id)
        }
      }
    )

    return realtimeSubscription
  }

  /**
   * Update device name
   */
  const updateDeviceName = async (id: string, name: string): Promise<boolean> => {
    return await updateDevice(id, { name })
  }

  /**
   * Cleanup subscriptions
   */
  const cleanup = (): void => {
    if (realtimeSubscription) {
      realtimeSubscription.unsubscribe()
      realtimeSubscription = null
    }
  }

  return {
    // State
    devices,
    discoveredDevices,
    deviceStatuses,
    isDiscovering,
    isLoading,
    isConnecting,
    error,
    
    // Getters
    allDevices,
    devicesWithStatus,
    onlineDevices,
    activeDevices,
    availableDiscovered,
    
    // Actions
    fetchDevices,
    discoverDevices,
    registerDevice,
    addDeviceByIp,
    updateDevice,
    updateDeviceName,
    updateDeviceStatus,
    removeDevice,
    checkDeviceConnectivity,
    getDevice,
    initRealtimeSubscription,
    cleanup,
    setError,
    clearError
  }
})
